import logger from '@stencil/logger';
import fetch from './fetch';
import sendToClientAnalytics from '@stencil/client-side-analytics/src/send-to-client-analytics';
/**
 * Objective of this module is to provide following features:
 * - [x] Simple wrapper for making network requests
 * - [ ] Latency Tracking
 * - [ ] Auto push GA analytics related to API endpoints
 * - [ ] Cancel if existing request over the air
 * - [ ] Retry as per specified count
 * - [ ] Short-Circuit request as per defined time
 * - [ ] Priority fetching
 * - [ ] No network response
 * - [ ] Logging utility logger/index.ts or logger.server.ts
 */

// https://developer.mozilla.org/en-US/docs/Web/API/AbortController/abort
// can implement fetch cancel request via AbortController, but require polyfill, AbortController also supported under node-fetch

export type xhrType = 'GET' | 'POST';

interface IXhrHelper<RequestDataGeneric> {
  name: string;
  type: xhrType;
  url: string;
  headers?: object;
  requestData?: object | string;
  credentials?: string;
  trackLatency?: {timeout: number; data?: object};
}

export default async function megatron<RequestDataGeneric, ResponseDataGeneric>({
  name,
  headers,
  type,
  url,
  requestData,
  credentials,
  trackLatency,
}: IXhrHelper<RequestDataGeneric>): Promise<ResponseDataGeneric> {
  let isResolved = false;

  if (trackLatency)
    setTimeout(() => {
      if (!isResolved) sendToClientAnalytics({apiName: name, ...trackLatency?.data});
    }, trackLatency.timeout);

  try {
    let params = requestData;

    var options = {
      headers,
      credentials: credentials || 'include',
      method: type,
    };

    if (type === 'POST') {
      options.body = params;
    }

    const request = new Request(url);
    const rawResponse = await fetch(request, options);
    const respJson = await rawResponse.json();
    isResolved = true;
    return respJson;
  } catch (err) {
    isResolved = true;
    logger.error({
      type: '@stencil/megatron',
      message: 'megatron call failed',
      data: {
        name,
        url,
      },
      error: err,
    });
    return {data: undefined};
  }
}

export async function get(url, {name, logLevel = 'verbose', headers = {}, credentials = ''}) {
  try {
    let options: {headers: Object; method: string; credentials?: string} = {
      headers,
      method: 'GET',
    };
    if (credentials) options = {...options, credentials};

    const response = await fetch(url, options);
    const responseJson = await response.json();
    logger[logLevel]({
      type: '@stencil/megatron',
      message: 'api response',
      data: {
        name,
        url,
        data: responseJson,
      },
    });
    return {data: responseJson};
  } catch (err) {
    logger.error({
      type: '@stencil/megatron',
      message: 'get call failed',
      data: {
        name,
        url,
      },
      error: err,
    });
    return {data: undefined};
  }
}
